import React, { useState } from "react";
import { useTranslation } from "react-i18next";

function generatePassword(options) {
  const { length, uppercase, lowercase, numbers, symbols } = options;
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const nums = "0123456789";
  const syms = "!@#$%^&*()-_=+[]{}|;:',.<>?/`~";
  let chars = "";
  if (uppercase) chars += upper;
  if (lowercase) chars += lower;
  if (numbers) chars += nums;
  if (symbols) chars += syms;
  if (!chars) return "";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

function PasswordGenerator() {
  const { t } = useTranslation();
  var passwordItems = t("PasswordGenerator", { returnObjects: true });
  const [length, setLength] = useState(16);
  const [uppercase, setUppercase] = useState(true);
  const [lowercase, setLowercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [generatedList, setGeneratedList] = useState(() => {
    try {
      const saved = localStorage.getItem("generatedPasswords");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const saveToLocalStorage = (list) => {
    localStorage.setItem("generatedPasswords", JSON.stringify(list));
  };

  const handleGenerate = () => {
    const newPassword = generatePassword({
      length,
      uppercase,
      lowercase,
      numbers,
      symbols,
    });
    setPassword(newPassword);
    setCopied(false);
  };

  const handleClearList = () => {
    setGeneratedList([]);
    saveToLocalStorage([]);
  };

  const handleCopy = () => {
    if (!password) return;
    navigator.clipboard.writeText(password);
    setCopied(true);
    // Salva apenas ao copiar
    if (password && generatedList[0] !== password) {
      const updatedList = [password, ...generatedList].slice(0, 50);
      setGeneratedList(updatedList);
      saveToLocalStorage(updatedList);
    }
    setTimeout(() => setCopied(false), 1500);
  };

  // Gera uma senha apenas na primeira renderização
  React.useEffect(() => {
    setPassword(generatePassword({ length, uppercase, lowercase, numbers, symbols }));
    // eslint-disable-next-line
  }, []);

  return (
    <div className="container-fluid password-generator py-5 mt-5">
      <div className="container">
        <div className="row">
          <div className="col-12 d-flex flex-column justify-content-center align-items-center first-section">
            <div className="col-12 d-flex justify-content-center title-container">
              <div className="title-row d-flex align-items-center justify-content-center">
                <div className="title">{passwordItems.Title}</div>
                <div className="animation-container-title"></div>
              </div>
            </div>
            <div className="col-12 text-center text-white-70 mt-2 mb-4 fs-5">
              {passwordItems.Subtitle}
            </div>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6 d-flex flex-column align-items-center gap-4 mt-3 second-section">
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control text-center fs-4"
                value={password}
                readOnly
                style={{ background: "#f8f9fa" }}
              />
              <button
                className={`btn btn-outline-secondary${
                  copied ? " bg-success text-white border-success" : ""
                }`}
                type="button"
                onClick={handleCopy}
                disabled={!password}
              >
                {copied ? passwordItems.Copied : passwordItems.Copy}
              </button>
            </div>
            <div className="w-100 d-flex flex-column gap-2">
              <label htmlFor="length" className="form-label">
                {passwordItems.LengthLabel}: {length}
              </label>
              <input
                type="range"
                className="form-range"
                min="4"
                max="64"
                value={length}
                id="length"
                onChange={(e) => setLength(Number(e.target.value))}
              />
            </div>
            <div className="row w-100 g-2">
              <div className="col-6 col-md-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="uppercase"
                    checked={uppercase}
                    onChange={() => setUppercase((v) => !v)}
                  />
                  <label className="form-check-label" htmlFor="uppercase">
                    {passwordItems.IncludeUppercaseLabel}
                  </label>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="lowercase"
                    checked={lowercase}
                    onChange={() => setLowercase((v) => !v)}
                  />
                  <label className="form-check-label" htmlFor="lowercase">
                    {passwordItems.IncludeLowercaseLabel}
                  </label>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="numbers"
                    checked={numbers}
                    onChange={() => setNumbers((v) => !v)}
                  />
                  <label className="form-check-label" htmlFor="numbers">
                    {passwordItems.IncludeNumbersLabel}
                  </label>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="symbols"
                    checked={symbols}
                    onChange={() => setSymbols((v) => !v)}
                  />
                  <label className="form-check-label" htmlFor="symbols">
                    {passwordItems.IncludeSpecialCharsLabel}
                  </label>
                </div>
              </div>
            </div>
            <button
              className="btn btn-primary w-100 mt-3"
              onClick={handleGenerate}
            >
              {passwordItems.GenerateButton}
            </button>
          </div>
        </div>
        <div className="row justify-content-center mt-4">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">
                {t(
                  "PasswordGenerator.GeneratedListLabel",
                  "Senhas geradas"
                )}
              </h5>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={handleClearList}
                disabled={generatedList.length === 0}
              >
                {t("PasswordGenerator.ClearListButton", "Limpar lista")}
              </button>
            </div>
            <textarea
              className="form-control"
              rows={5}
              value={generatedList.join("\n")}
              readOnly
              style={{ fontFamily: "monospace", fontSize: 16 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordGenerator;
